/**
 * Sing4YouAudiencePage — pure viewer page
 *
 * This is the page the QR code on Sing4YouCafePage links to.
 * Route: /sing4you-interaction/:channel_name?bookingId=...
 *
 * WHAT THIS PAGE DOES:
 *   - Joins the Agora RTC channel as audience (receives talent's video + audio)
 *   - Joins the Agora RTM channel (receives karaoke_sync, pvlc_sync etc.)
 *   - Sends state_request 1500ms after joining so talent broadcasts current state
 *   - Shows the live stream (Sing4YouLiveStream) with karaoke + PVLC overlays
 *   - Shows live chat (LiveChat)
 *   - Shows emoji reactions (fires RTM reaction + sendReaction API)
 *   - Shows YouTube song request (YoutubeRequest)
 *   - Mobile-first layout: full-screen video, slide-up panel for chat/reactions
 *
 * WHAT THIS PAGE DOES NOT DO (host-only features, excluded intentionally):
 *   - Guest management
 *   - QR code sharing
 *   - Host badge / monitoring label
 *   - Stream controls (mic, camera, end session)
 *
 * ARCHITECTURE:
 *   Same RTM + streamKey + pvlcRef pattern as Sing4YouCafePage.
 *   VideoTracker has no onNetworkError prop (B7 fix).
 *   state_request delay 1500ms (B8 fix).
 *   PVLC state stored in useRef so it survives streamKey remounts.
 */

import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { useRemoteUsers, useRemoteVideoTracks } from "agora-rtc-react";
import AgoraRTM from "agora-rtm-sdk";
import { enqueueSnackbar } from "notistack";

import { useAuth } from "../../../hooks";
import { AGORA_RTM_APP_ID } from "../../../constants/config";
import { getAgoraRtmToken } from "../../../services/api/agora.api";
import { useSendReaction, ReactionType } from "../../../services/stagemode_api/viewers/viewers_api";
import AgoraWrapper from "../Karaokecard/AgoraWrapper_updated";
import Sing4YouLiveStream from "./Sing4YouLiveStream_bug_fixed_PVLC";
import KaraokeLyrics from "../Karaokecard/Agora/KaraokeLyrics";
import LiveChat from "../LiveChat";
import YoutubeRequest from "../Talent/YoutubeRequest";
import SocketHandler from "../../../utils/socketHandler_karokelyrics";

import "../../../styles/sing4you-live.css";

/* ── Reaction map ────────────────────────────────────────────────────────── */
const REACTIONS: { emoji: string; type: ReactionType; label: string }[] = [
  { emoji: "🔥", type: "Like",  label: "Fire"    },
  { emoji: "❤️", type: "Heart", label: "Love"    },
  { emoji: "🎤", type: "Clap",  label: "Applaud" },
  { emoji: "⭐", type: "Star",  label: "Star"    },
  { emoji: "🎵", type: "Like",  label: "Vibe"    },
  { emoji: "🎶", type: "Clap",  label: "Music"   },
  { emoji: "🎼", type: "Like",  label: "Tune"    },
  { emoji: "💕", type: "Heart", label: "Hearts"  },
];

type PanelTab = "chat" | "reactions" | "request";

/* ── VideoTracker (no onNetworkError — B7 fix) ───────────────────────────── */
function VideoTracker() {
  const remoteUsers = useRemoteUsers();
  const { videoTracks } = useRemoteVideoTracks(remoteUsers);

  useEffect(() => {
    if (!videoTracks.length) return;
    const cleanups: (() => void)[] = [];
    const LAYOUT_REASONS = ["SIZE", "STYLE", "POSITION", "COVERED"];

    videoTracks.forEach((track) => {
      const current = (track as any).getVideoElementVisibleStatus?.() as
        | { visible: boolean; reason?: string } | undefined;
      if (current && !current.visible && LAYOUT_REASONS.includes(current.reason ?? "")) {
        try { (track as any).play?.(); } catch {}
      }
      const handler = ({ visible, reason }: { visible: boolean; reason?: string }) => {
        if (!visible && LAYOUT_REASONS.includes(reason ?? "")) {
          try { (track as any).play?.(); } catch {}
        }
      };
      (track as any).on("video-element-visible-status", handler);
      cleanups.push(() => (track as any).off("video-element-visible-status", handler));
    });

    return () => cleanups.forEach((fn) => fn());
  }, [videoTracks]);

  return null;
}

/* ── Inner (must be inside AgoraRTCProvider via AgoraWrapper) ─────────────── */
function Sing4YouAudienceInner() {
  const { channel_name = "" } = useParams();
  const navigate              = useNavigate();
  const { userInfo }          = useAuth();
  const [params]              = useSearchParams();
  const bookingId             = params.get("bookingId") ?? "";

  const [activeTab,     setActiveTab]     = useState<PanelTab>("chat");
  const [panelOpen,     setPanelOpen]     = useState(false);
  const [chatInput,     setChatInput]     = useState("");
  const [viewerCount,   setViewerCount]   = useState(0);
  const [lastEmoji,     setLastEmoji]     = useState<string | null>(null);
  const [isFullscreen,  setIsFullscreen]  = useState(false);

  /* ── streamKey — bumped ONLY by genuine NETWORK_ERROR ──────────────────── */
  const [streamKey,       setStreamKey]       = useState(0);
  const bumpTimer                             = useRef<ReturnType<typeof setTimeout> | null>(null);
  const bumpRevision = useCallback(() => {
    if (bumpTimer.current) return;
    bumpTimer.current = setTimeout(() => {
      setStreamKey((k) => k + 1);
      bumpTimer.current = null;
    }, 500);
  }, []);

  /* ── Karaoke state ────────────────────────────────────────────────────── */
  const [hostLyricsLrc,  setHostLyricsLrc]  = useState("");
  const [hostLyricsTime, setHostLyricsTime] = useState(0);
  const [hostIsPlaying,  setHostIsPlaying]  = useState(false);
  const [hostSongTitle,  setHostSongTitle]  = useState("");
  const [hostDuration,   setHostDuration]   = useState(0);

  /* ── PVLC state in useRef (survives streamKey remounts) ──────────────── */
  const pvlcRef = useRef({ active: false, title: "", time: 0, playing: false, duration: 0 });
  const [pvlcRenderTick, setPvlcRenderTick] = useState(0);
  const tickPvlc = useCallback(() => setPvlcRenderTick((n) => n + 1), []);

  /* ── Flying emoji reactions ───────────────────────────────────────────── */
  const [flyingEmojis, setFlyingEmojis] = useState<{ id: number; emoji: string; x: number }[]>([]);
  const emojiIdRef = useRef(0);

  /* ── RTM refs ────────────────────────────────────────────────────────── */
  const rtmClientRef   = useRef<ReturnType<typeof AgoraRTM.createInstance> | null>(null);
  const rtmChannelRef  = useRef<ReturnType<ReturnType<typeof AgoraRTM.createInstance>["createChannel"]> | null>(null);
  const rtmMountedRef  = useRef(false);
  const rtmRetryCount  = useRef(0);
  const rtmRetryTimer  = useRef<ReturnType<typeof setTimeout> | null>(null);
  const fullscreenRef  = useRef<HTMLDivElement>(null);

  /* ── Stable viewer ID (avoids RTM login rejection on rapid refresh) ────── */
  const viewerUserId = useMemo(
    () => `aud_${userInfo?.id ?? "anon"}_${Date.now()}`,
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [],
  );

  const displayName = userInfo?.username ?? "viewer";

  const { mutateAsync: sendReaction } = useSendReaction(bookingId);
  const handleCallEnd = useCallback(() => navigate("/"), [navigate]);

  /* ── Fullscreen ──────────────────────────────────────────────────────── */
  useEffect(() => {
    const h = () => setIsFullscreen(!!document.fullscreenElement);
    document.addEventListener("fullscreenchange", h);
    return () => document.removeEventListener("fullscreenchange", h);
  }, []);

  const toggleFullscreen = useCallback(() => {
    if (isFullscreen) document.exitFullscreen().catch(() => {});
    else fullscreenRef.current?.requestFullscreen().catch(() => {});
  }, [isFullscreen]);

  /* ── Emoji reaction ──────────────────────────────────────────────────── */
  const handleReact = useCallback(async (emoji: string, type: ReactionType) => {
    try {
      setLastEmoji(emoji);
      setTimeout(() => setLastEmoji(null), 420);

      // Animate locally
      const id = ++emojiIdRef.current;
      const x  = 10 + Math.random() * 70;
      setFlyingEmojis((prev) => [...prev, { id, emoji, x }]);
      setTimeout(() => setFlyingEmojis((prev) => prev.filter((e) => e.id !== id)), 2500);

      // API + RTM broadcast so talent and other viewers see it
      await sendReaction({ type, status: 1 });
      rtmChannelRef.current?.sendMessage({
        text: JSON.stringify({ type: "reaction", emoji }),
      }).catch(() => {});
    } catch {}
  }, [sendReaction]);

  /* ── RTM setup ───────────────────────────────────────────────────────── */
  const setupRTM = useCallback(async () => {
    if (!AGORA_RTM_APP_ID || !channel_name) return;
    try {
      await rtmChannelRef.current?.leave().catch(() => {});
      await rtmClientRef.current?.logout().catch(() => {});
      rtmChannelRef.current = null;
      rtmClientRef.current  = null;

      const rtmToken = await getAgoraRtmToken(viewerUserId).catch(() => null);
      const client = AgoraRTM.createInstance(AGORA_RTM_APP_ID);
      try {
        await client.login({ uid: viewerUserId, token: rtmToken ?? undefined });
      } catch (loginErr: any) {
        const code = loginErr?.code ?? loginErr?.errorCode;
        if (code === 5) {
          enqueueSnackbar("Chat sync requires secure login. Please reload or try again.", { variant: "warning", autoHideDuration: 6000 });
        } else {
          enqueueSnackbar("Could not connect to chat. Reload to try again.", { variant: "warning", autoHideDuration: 5000 });
        }
        throw loginErr;
      }
      if (!rtmMountedRef.current) { client.logout().catch(() => {}); return; }

      const ch = client.createChannel(channel_name);

      ch.on("ChannelMessage", (message: any) => {
        if (!rtmMountedRef.current) return;
        try {
          const msg = JSON.parse(message.text ?? "{}");

          // Karaoke sync
          if (msg.type === "karaoke_load") {
            setHostLyricsLrc(msg.lrc ?? "");
            setHostSongTitle(msg.title ?? "");
            setHostDuration(msg.duration ?? 0);
            setHostLyricsTime(msg.time ?? 0);
          } else if (msg.type === "karaoke_sync") {
            setHostLyricsTime(msg.time ?? 0);
            setHostIsPlaying(!!msg.playing);
          } else if (msg.type === "karaoke_end") {
            setHostLyricsLrc("");
            setHostSongTitle("");
            setHostDuration(0);
            setHostLyricsTime(0);
            setHostIsPlaying(false);
          }

          // PVLC sync — useRef so it survives streamKey remounts
          else if (msg.type === "pvlc_load") {
            pvlcRef.current.active   = true;
            pvlcRef.current.title    = msg.title    ?? "";
            pvlcRef.current.duration = msg.duration ?? 0;
            tickPvlc();
          } else if (msg.type === "pvlc_sync") {
            pvlcRef.current.time     = msg.time     ?? 0;
            pvlcRef.current.playing  = msg.playing  ?? false;
            pvlcRef.current.duration = msg.duration ?? 0;
            tickPvlc();
          } else if (msg.type === "pvlc_end") {
            pvlcRef.current = { active: false, title: "", time: 0, playing: false, duration: 0 };
            tickPvlc();
          }

          // Incoming reactions from talent or other viewers
          else if (msg.type === "reaction" && msg.emoji) {
            const id = ++emojiIdRef.current;
            const x  = 10 + Math.random() * 70;
            setFlyingEmojis((prev) => [...prev, { id, emoji: msg.emoji, x }]);
            setTimeout(() => setFlyingEmojis((prev) => prev.filter((e) => e.id !== id)), 2500);
          }
        } catch {}
      });

      await ch.join();
      rtmRetryCount.current = 0;

      // 1500ms delay so RTC has time to join before state snapshot arrives (B8 fix)
      setTimeout(() => {
        if (!rtmMountedRef.current) return;
        ch.sendMessage({ text: JSON.stringify({ type: "state_request" }) }).catch(() => {});
      }, 1500);

      const updateCount = async () => {
        try { const m = await ch.getMembers(); if (rtmMountedRef.current) setViewerCount(m.length); } catch {}
      };
      ch.on("MemberJoined", updateCount);
      ch.on("MemberLeft",   updateCount);
      updateCount();

      // Exponential backoff reconnect: 3s → 6s → 12s → 24s → 48s
      client.on("ConnectionStateChanged", (newState: string) => {
        if (newState === "DISCONNECTED" && rtmMountedRef.current) {
          if (rtmRetryCount.current >= 5) {
            enqueueSnackbar("⚠️ Chat disconnected — reload to restore", { variant: "warning", autoHideDuration: 6000 });
            return;
          }
          const delay = 3000 * Math.pow(2, rtmRetryCount.current);
          rtmRetryCount.current += 1;
          rtmRetryTimer.current = setTimeout(() => { if (rtmMountedRef.current) setupRTM(); }, delay);
        }
      });

      rtmClientRef.current  = client;
      rtmChannelRef.current = ch;
    } catch (err) {
      console.warn("[S4Y Audience][RTM]", err);
    }
  }, [viewerUserId, channel_name, tickPvlc, enqueueSnackbar]);

  useEffect(() => {
    rtmMountedRef.current = true;
    setupRTM();
    return () => {
      rtmMountedRef.current = false;
      if (rtmRetryTimer.current) clearTimeout(rtmRetryTimer.current);
      rtmChannelRef.current?.leave().catch(() => {});
      rtmClientRef.current?.logout().catch(() => {});
    };
  }, [setupRTM]);

  /* ── Local clock interpolation between 1s RTM syncs ─────────────────── */
  useEffect(() => {
    if (!hostIsPlaying) return;
    let lastTick = Date.now();
    const id = setInterval(() => {
      const now   = Date.now();
      const delta = (now - lastTick) / 1000;
      lastTick    = now;
      setHostLyricsTime((t) => hostDuration > 0 && t >= hostDuration ? t : t + delta);
    }, 100);
    return () => clearInterval(id);
  }, [hostIsPlaying, hostDuration]);

  /* ── Socket: pre-load LRC ────────────────────────────────────────────── */
  useEffect(() => {
    if (!bookingId) return;
    const socketHandler = SocketHandler.getInstance();
    socketHandler.subscribeToSing4YouLyrics(bookingId, async (data: any) => {
      if (data?.title    !== undefined) setHostSongTitle(data.title    ?? "");
      if (data?.duration !== undefined) setHostDuration(data.duration  ?? 0);
      const load = (lrcText: string) => { setHostLyricsLrc(lrcText); setHostLyricsTime(0); };
      if (data?.lrc) {
        load(data.lrc);
      } else if (data?.lyrics_url) {
        try { const res = await fetch(data.lyrics_url); if (!res.ok) throw new Error(""); load(await res.text()); }
        catch {}
      }
    });
    return () => { socketHandler.unsubscribeFromSing4YouLyrics(bookingId); };
  }, [bookingId]);

  /* ─────────────────────────────── RENDER ─────────────────────────────── */
  const pvlc = pvlcRef.current;

  return (
    <div
      ref={fullscreenRef}
      style={{
        width: "100%", height: "100vh",
        background: "#0a0a18",
        display: "flex", flexDirection: "column",
        overflow: "hidden", position: "relative",
      }}
    >
      {/* ── Top bar ────────────────────────────────────────────────────── */}
      <div style={{
        display: "flex", alignItems: "center", justifyContent: "space-between",
        padding: "10px 16px", flexShrink: 0,
        background: "rgba(0,0,0,0.5)", backdropFilter: "blur(8px)",
        borderBottom: "1px solid rgba(255,255,255,0.06)", zIndex: 20,
      }}>
        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
          <span style={{ fontSize: "1.15rem" }}>🎤</span>
          <span style={{ color: "#fff", fontWeight: 700, fontSize: "0.88rem", letterSpacing: "0.06em" }}>SING4YOU</span>
          {hostSongTitle && (
            <span style={{ color: "rgba(255,255,255,0.45)", fontSize: "0.75rem", marginLeft: 4 }}>♪ {hostSongTitle}</span>
          )}
        </div>
        <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
          <span style={{ display: "flex", alignItems: "center", gap: 4, color: "rgba(255,255,255,0.55)", fontSize: "0.78rem" }}>
            <span style={{ width: 6, height: 6, borderRadius: "50%", background: "#ef4444", display: "inline-block", boxShadow: "0 0 6px rgba(239,68,68,0.8)" }} />
            {viewerCount} watching
          </span>
        </div>
      </div>

      {/* ── Video area ─────────────────────────────────────────────────── */}
      <div style={{ flex: 1, position: "relative", minHeight: 0 }}>
        <AgoraWrapper mode="live" role="audience">
          {/* VideoTracker — layout recovery only, never bumps streamKey */}
          <VideoTracker />
          <Sing4YouLiveStream
            key={streamKey}
            userId={viewerUserId}
            videoId={channel_name}
            displayName={displayName}
            onCallEnd={handleCallEnd}
            onNetworkError={bumpRevision}
            karaokeActive={hostIsPlaying}
            karaokeTrackName={hostSongTitle}
            pvlcActive={pvlc.active}
            pvlcTitle={pvlc.title}
            pvlcTime={pvlc.time}
            pvlcPlaying={pvlc.playing}
            pvlcDuration={pvlc.duration}
            isFullscreen={isFullscreen}
            onToggleFullscreen={toggleFullscreen}
          />
        </AgoraWrapper>

        {/* Flying emojis */}
        {flyingEmojis.map(({ id, emoji, x }) => (
          <span
            key={id}
            className="s4y-flying-emoji"
            style={{ left: `${x}%`, bottom: "20%", position: "absolute", zIndex: 15, pointerEvents: "none" }}
          >
            {emoji}
          </span>
        ))}

        {/* Quick-react strip — always visible over the video */}
        <div style={{
          position: "absolute", bottom: 16, left: "50%", transform: "translateX(-50%)",
          display: "flex", gap: 6, zIndex: 15,
          background: "rgba(0,0,0,0.55)", backdropFilter: "blur(10px)",
          borderRadius: 999, padding: "8px 14px",
          border: "1px solid rgba(255,255,255,0.1)",
        }}>
          {REACTIONS.map(({ emoji, type, label }) => (
            <button
              key={emoji}
              title={label}
              onClick={() => handleReact(emoji, type)}
              style={{
                background: lastEmoji === emoji ? "rgba(255,255,255,0.2)" : "transparent",
                border: "none", borderRadius: "50%",
                width: 36, height: 36, fontSize: "1.1rem",
                cursor: "pointer", transition: "transform 0.1s, background 0.15s",
                display: "flex", alignItems: "center", justifyContent: "center",
                transform: lastEmoji === emoji ? "scale(1.25)" : "scale(1)",
              }}
            >
              {emoji}
            </button>
          ))}
        </div>

        {/* Panel toggle button */}
        <button
          onClick={() => setPanelOpen((p) => !p)}
          style={{
            position: "absolute", bottom: 72, right: 16, zIndex: 15,
            background: panelOpen ? "#ff6b9d" : "rgba(0,0,0,0.6)",
            border: `1px solid ${panelOpen ? "transparent" : "rgba(255,255,255,0.2)"}`,
            backdropFilter: "blur(8px)",
            borderRadius: 12, padding: "8px 14px",
            color: "#fff", fontWeight: 600, fontSize: "0.8rem",
            cursor: "pointer", transition: "all 0.2s",
            display: "flex", alignItems: "center", gap: 6,
          }}
        >
          {panelOpen ? "✕ Close" : "💬 Chat"}
        </button>
      </div>

      {/* ── Karaoke lyrics strip ────────────────────────────────────────── */}
      {hostLyricsLrc && (
        <div style={{ flexShrink: 0 }}>
          <KaraokeLyrics
            lrc={hostLyricsLrc}
            currentTime={hostLyricsTime}
            duration={hostDuration}
            songTitle={hostSongTitle || undefined}
            isPlaying={hostIsPlaying}
          />
        </div>
      )}

      {/* ── Slide-up panel ─────────────────────────────────────────────── */}
      <div style={{
        position: "absolute", bottom: 0, left: 0, right: 0, zIndex: 30,
        background: "#0d1021",
        border: "1px solid rgba(255,255,255,0.08)",
        borderRadius: "16px 16px 0 0",
        transform: panelOpen ? "translateY(0)" : "translateY(100%)",
        transition: "transform 0.3s cubic-bezier(0.32,0.72,0,1)",
        maxHeight: "55vh",
        display: "flex", flexDirection: "column",
      }}>
        {/* Panel drag handle */}
        <div style={{ display: "flex", justifyContent: "center", padding: "10px 0 6px" }}>
          <div style={{ width: 36, height: 4, borderRadius: 2, background: "rgba(255,255,255,0.2)" }} />
        </div>

        {/* Panel tabs */}
        <div style={{ display: "flex", borderBottom: "1px solid rgba(255,255,255,0.07)", flexShrink: 0 }}>
          {([
            { id: "chat",      label: "Chat"     },
            { id: "reactions", label: "React"    },
            { id: "request",   label: "Request"  },
          ] as { id: PanelTab; label: string }[]).map(({ id, label }) => (
            <button
              key={id}
              onClick={() => setActiveTab(id)}
              style={{
                flex: 1, padding: "10px 0",
                background: "transparent", border: "none",
                borderBottom: activeTab === id ? "2px solid #ff6b9d" : "2px solid transparent",
                color: activeTab === id ? "#fff" : "rgba(255,255,255,0.45)",
                fontWeight: activeTab === id ? 600 : 400,
                fontSize: "0.82rem", cursor: "pointer", transition: "all 0.15s",
              }}
            >
              {label}
            </button>
          ))}
        </div>

        {/* Panel content */}
        <div style={{ flex: 1, overflowY: "auto", minHeight: 0 }}>

          {/* Chat */}
          {activeTab === "chat" && (
            <div style={{ height: "100%", display: "flex", flexDirection: "column" }}>
              {bookingId ? (
                <LiveChat bookingId={bookingId} username={displayName} height="100%" />
              ) : (
                <div style={{ padding: 24, textAlign: "center", color: "rgba(255,255,255,0.4)", fontSize: "0.82rem" }}>
                  No active session
                </div>
              )}
            </div>
          )}

          {/* Reactions (full grid) */}
          {activeTab === "reactions" && (
            <div style={{ padding: "16px 20px" }}>
              <div style={{ color: "rgba(255,255,255,0.5)", fontSize: "0.72rem", textTransform: "uppercase", letterSpacing: 1, marginBottom: 14 }}>
                Tap to react — everyone will see it
              </div>
              <div style={{ display: "grid", gridTemplateColumns: "repeat(4, 1fr)", gap: 10 }}>
                {REACTIONS.map(({ emoji, type, label }) => (
                  <button
                    key={emoji}
                    title={label}
                    onClick={() => handleReact(emoji, type)}
                    style={{
                      display: "flex", flexDirection: "column", alignItems: "center", gap: 5,
                      padding: "14px 10px",
                      background: lastEmoji === emoji ? "rgba(255,107,157,0.18)" : "rgba(255,255,255,0.05)",
                      border: `1px solid ${lastEmoji === emoji ? "rgba(255,107,157,0.5)" : "rgba(255,255,255,0.1)"}`,
                      borderRadius: 12, fontSize: "1.5rem", cursor: "pointer",
                      transition: "all 0.15s",
                      transform: lastEmoji === emoji ? "scale(1.08)" : "scale(1)",
                    }}
                  >
                    {emoji}
                    <span style={{ color: "rgba(255,255,255,0.45)", fontSize: "0.65rem" }}>{label}</span>
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* Song request */}
          {activeTab === "request" && (
            <div style={{ padding: "4px 0" }}>
              {bookingId ? (
                <YoutubeRequest bookingId={bookingId} />
              ) : (
                <div style={{ padding: 24, textAlign: "center", color: "rgba(255,255,255,0.4)", fontSize: "0.82rem" }}>
                  No active session
                </div>
              )}
            </div>
          )}
        </div>

        {/* Chat input — only shown in chat tab */}
        {activeTab === "chat" && (
          <div style={{ display: "flex", gap: 8, padding: "10px 14px", borderTop: "1px solid rgba(255,255,255,0.07)", flexShrink: 0 }}>
            <input
              className="s4y-chat-input"
              placeholder="Say something..."
              value={chatInput}
              onChange={(e) => setChatInput(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter" && chatInput.trim()) {
                  rtmChannelRef.current?.sendMessage({ text: JSON.stringify({ type: "chat", text: chatInput.trim(), from: displayName }) }).catch(() => {});
                  setChatInput("");
                }
              }}
              style={{ flex: 1 }}
            />
            <button
              className="s4y-chat-send"
              onClick={() => {
                if (!chatInput.trim()) return;
                rtmChannelRef.current?.sendMessage({ text: JSON.stringify({ type: "chat", text: chatInput.trim(), from: displayName }) }).catch(() => {});
                setChatInput("");
              }}
            >
              <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor">
                <path d="M2.01 21L23 12 2.01 3 2 10l15 2-15 2z" />
              </svg>
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

/* ── Exported page (wraps with AgoraWrapper as audience) ─────────────────── */
export function Sing4YouAudiencePage() {
  return (
    <AgoraWrapper mode="live" role="audience">
      <Sing4YouAudienceInner />
    </AgoraWrapper>
  );
}

export default Sing4YouAudiencePage;